/**
 * React App Entry Point
 */

import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import App from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ThemeProvider } from './contexts/ThemeContext';
import { ActivityProvider } from './contexts/ActivityContext';
import { HotkeyProvider } from './lib/hotkeys';
import { sendClientLog } from './lib/clientLog';
import { isGateUnlocked as checkGateUnlocked, unlockGate } from './lib/storage';
import './globals.css';
import './styles.css';

// ── Global error handlers (fire before React mounts) ───────────────
window.onerror = (_msg, source, lineno, colno, error) => {
  sendClientLog('error', error?.message || String(_msg), {
    source: `${source || 'unknown'}:${lineno}:${colno}`,
    stack: error?.stack,
  });
};

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const message = reason instanceof Error ? reason.message : String(reason);
  sendClientLog('error', message, {
    source: 'unhandledrejection',
    stack: reason instanceof Error ? reason.stack : undefined,
  });
});

// Router basename for subdirectory deployment (e.g., "/decoy_folder/dashboard")
const routerBasename = import.meta.env.VITE_ROUTER_BASE || '';
const isMultiTenant = import.meta.env.VITE_MULTI_TENANT === 'true';
const MT_GATE_HASH = import.meta.env.VITE_DASHBOARD_PASSWORD_HASH || '';

// Lazy-load GateView for MT deployment-level gate
const GateViewLazy = lazy(() =>
  import('./components/pages/GateView').then((m) => ({ default: m.GateView }))
);

function Loading() {
  return (
    <div className="bg-background flex min-h-screen items-center justify-center">
      <div className="text-muted-foreground text-sm">Loading...</div>
    </div>
  );
}

/**
 * App routes — used for both ST and MT modes.
 *
 * In MT mode, App's default export is MTApp (auth wrapper around the full shell).
 * MTApp handles JWT login internally, so all routes just render <App />.
 */
function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/"
        element={<App />}
      />
      <Route
        path="/app"
        element={<App />}
      />
      <Route
        path="/app/explore"
        element={<App />}
      />
      <Route
        path="/app/explore/:category"
        element={<App />}
      />
      <Route
        path="/app/explore/:category/:series"
        element={<App />}
      />
      <Route
        path="/app/explore/:category/:series/:event"
        element={<App />}
      />
      <Route
        path="/app/market/:ticker"
        element={<App />}
      />
      <Route
        path="/app/:view"
        element={<App />}
      />
      <Route
        path="*"
        element={
          <Navigate
            to="/"
            replace
          />
        }
      />
    </Routes>
  );
}

/**
 * MT wrapper: optional deployment-level gate before AppRoutes.
 * JWT auth is handled inside MTApp (App.tsx default export in MT mode).
 */
function MultiTenantApp() {
  const [gateUnlocked, setGateUnlocked] = React.useState(() =>
    MT_GATE_HASH ? checkGateUnlocked(MT_GATE_HASH) : true
  );

  const handleGateUnlock = React.useCallback((verifiedHash: string, remember: boolean) => {
    unlockGate(verifiedHash, remember);
    setGateUnlocked(true);
  }, []);

  if (!gateUnlocked) {
    return (
      <Suspense fallback={<Loading />}>
        <GateViewLazy onUnlock={handleGateUnlock} />
      </Suspense>
    );
  }

  return <AppRoutes />;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider>
      <ActivityProvider>
        <HotkeyProvider>
          <ErrorBoundary>
            <BrowserRouter basename={routerBasename}>
              {isMultiTenant ? <MultiTenantApp /> : <AppRoutes />}
            </BrowserRouter>
          </ErrorBoundary>
        </HotkeyProvider>
      </ActivityProvider>
    </ThemeProvider>
  </React.StrictMode>
);
